import { useState, useMemo } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { Calculator, Users, Building2, Clock, TrendingUp, ArrowRight, ChevronDown } from 'lucide-react';

const TEAM_OPTIONS = [
  { label: '10-25', value: 20, hours: 15 },
  { label: '26-50', value: 40, hours: 25 },
  { label: '51-100', value: 75, hours: 45 },
  { label: '101-250', value: 175, hours: 80 },
  { label: '251-500', value: 375, hours: 140 },
  { label: '500+', value: 600, hours: 200 },
];

const DEPARTMENTS = [
  { id: 'executive', label: 'Executive / Leadership', impact: 8, hours: 12 },
  { id: 'sales', label: 'Sales & Business Dev', impact: 7, hours: 15 },
  { id: 'marketing', label: 'Marketing & Content', impact: 6, hours: 10 },
  { id: 'operations', label: 'Operations & Projects', impact: 8, hours: 18 },
  { id: 'finance', label: 'Finance & Reporting', impact: 6, hours: 12 },
  { id: 'legal', label: 'Legal & Compliance', impact: 9, hours: 20 },
  { id: 'research', label: 'Research & Intelligence', impact: 7, hours: 14 },
  { id: 'support', label: 'Customer Service', impact: 5, hours: 10 },
  { id: 'procurement', label: 'Procurement & Vendors', impact: 5, hours: 8 },
  { id: 'private', label: 'Private Office / Family Office', impact: 9, hours: 16 },
];

const USE_CASES = [
  { id: 'briefings', label: 'Executive briefings & summaries', factor: 1.2 },
  { id: 'followup', label: 'Follow-up & task coordination', factor: 1.3 },
  { id: 'documents', label: 'Document review & preparation', factor: 1.4 },
  { id: 'research', label: 'Research & intelligence gathering', factor: 1.2 },
  { id: 'reporting', label: 'Internal reporting & overviews', factor: 1.1 },
  { id: 'coordination', label: 'Cross-team coordination', factor: 1.3 },
  { id: 'compliance', label: 'Compliance & governance support', factor: 1.2 },
  { id: 'client', label: 'Client & vendor management', factor: 1.1 },
];

export default function ROICalculator() {
  const [teamIdx, setTeamIdx] = useState(2);
  const [selectedDepts, setSelectedDepts] = useState(['executive', 'sales', 'operations']);
  const [selectedCases, setSelectedCases] = useState(['briefings', 'followup', 'research']);
  const [showResults, setShowResults] = useState(false);

  const toggleDept = (id) => {
    setSelectedDepts(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  };
  const toggleCase = (id) => {
    setSelectedCases(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const results = useMemo(() => {
    const team = TEAM_OPTIONS[teamIdx];
    const depts = DEPARTMENTS.filter(d => selectedDepts.includes(d.id));
    const cases = USE_CASES.filter(c => selectedCases.includes(c.id));

    const totalHoursWeek = depts.reduce((acc, d) => acc + d.hours, 0);
    const caseFactor = cases.reduce((acc, c) => acc + c.factor, 0) / Math.max(cases.length, 1);
    const savingsPercent = Math.min(35 + depts.length * 3 + cases.length * 2, 65);
    const hoursSaved = Math.round(totalHoursWeek * (savingsPercent / 100) * caseFactor);
    const avgImpact = depts.reduce((acc, d) => acc + d.impact, 0) / Math.max(depts.length, 1);
    const efficiencyGain = Math.round(savingsPercent * caseFactor);
    const yearHours = hoursSaved * 48;

    return { totalHoursWeek, hoursSaved, savingsPercent, avgImpact, efficiencyGain, yearHours, deptCount: depts.length, caseCount: cases.length };
  }, [teamIdx, selectedDepts, selectedCases]);

  return (
    <section id="roi-calculator" className="relative py-24 sm:py-32 overflow-hidden" data-testid="roi-calculator-section">
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-30" />
      <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-aurocle-gold/4 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-12">
            <div className="overline mb-4">Impact Assessment</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
              Agent ROI Calculator
            </h2>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mx-auto">
              Estimate the operational impact of deploying a private multi-agent workforce across your organization.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="glass-card border-aurocle-gold/10 overflow-hidden">
            <div className="p-8 lg:p-10">
              {/* Step 1: Team Size */}
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={16} className="text-aurocle-gold" />
                  <span className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60">Step 1</span>
                  <span className="text-sm font-heading text-aurocle-text">Team Size</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {TEAM_OPTIONS.map((opt, i) => (
                    <button key={i} onClick={() => setTeamIdx(i)} data-testid={`team-size-${i}`}
                      className={`py-3 px-4 text-sm font-mono text-center transition-all border ${
                        teamIdx === i ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/6 text-aurocle-text-secondary hover:border-white/15'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Departments */}
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={16} className="text-aurocle-gold" />
                  <span className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60">Step 2</span>
                  <span className="text-sm font-heading text-aurocle-text">Departments to Support</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {DEPARTMENTS.map((dept) => (
                    <button key={dept.id} onClick={() => toggleDept(dept.id)} data-testid={`dept-${dept.id}`}
                      className={`py-2.5 px-3 text-xs text-left transition-all border ${
                        selectedDepts.includes(dept.id) ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/6 text-aurocle-text-secondary hover:border-white/15'}`}>
                      {dept.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Use Cases */}
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={16} className="text-aurocle-gold" />
                  <span className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60">Step 3</span>
                  <span className="text-sm font-heading text-aurocle-text">Primary Use Cases</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  {USE_CASES.map((uc) => (
                    <button key={uc.id} onClick={() => toggleCase(uc.id)} data-testid={`uc-${uc.id}`}
                      className={`py-2.5 px-3 text-xs text-left transition-all border ${
                        selectedCases.includes(uc.id) ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/6 text-aurocle-text-secondary hover:border-white/15'}`}>
                      {uc.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              {!showResults && (
                <div className="text-center">
                  <button onClick={() => setShowResults(true)} className="btn-gold cta-burning" data-testid="calculate-roi-btn">
                    <Calculator size={14} /> Calculate Impact
                  </button>
                </div>
              )}

              {/* Results */}
              {showResults && (
                <div className="mt-8 pt-8 border-t border-white/5" data-testid="roi-results">
                  <div className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60 mb-6 text-center">Estimated Impact</div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                      { label: 'Weekly Hours Recovered', value: `${results.hoursSaved}h`, icon: Clock },
                      { label: 'Annual Hours Saved', value: `${results.yearHours.toLocaleString()}h`, icon: TrendingUp },
                      { label: 'Efficiency Gain', value: `${results.efficiencyGain}%`, icon: Calculator },
                      { label: 'Departments Covered', value: results.deptCount, icon: Building2 },
                    ].map((stat, i) => {
                      const StatIcon = stat.icon;
                      return (
                        <div key={i} className="p-5 bg-aurocle-base/50 border border-aurocle-gold/10 text-center agent-working">
                          <StatIcon size={16} className="text-aurocle-gold mx-auto mb-2" />
                          <div className="text-2xl font-heading font-medium text-aurocle-text">{stat.value}</div>
                          <div className="text-[10px] font-mono text-aurocle-text-secondary mt-1 uppercase tracking-wider">{stat.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="glass-card p-6 border-aurocle-gold/10 mb-6">
                    <p className="text-sm text-aurocle-text leading-relaxed text-center">
                      Based on a team of <span className="text-aurocle-gold font-medium">{TEAM_OPTIONS[teamIdx].label}</span> with
                      <span className="text-aurocle-gold font-medium"> {results.deptCount} departments</span> and
                      <span className="text-aurocle-gold font-medium"> {results.caseCount} use cases</span>,
                      a private multi-agent workforce could recover approximately
                      <span className="text-aurocle-gold font-medium"> {results.hoursSaved} hours per week</span> of
                      operational capacity — equivalent to
                      <span className="text-aurocle-gold font-medium"> {results.yearHours.toLocaleString()} hours annually</span>.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a href="#contact" className="btn-gold cta-burning" data-testid="roi-cta-demo">
                      Discuss Your Architecture <ArrowRight size={14} />
                    </a>
                    <button onClick={() => setShowResults(false)} className="btn-outline text-xs">
                      Recalculate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
